import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import { ClickableEventLink } from "./ClickableEventLink";
import { EventPreviewDialog } from "./EventPreviewDialog";
import { useEventTitles } from "@/hooks/useEventTitles";

interface Interaction {
  id: string;
  user_id: string | null;
  session_id: string;
  interaction_type: string;
  target: string;
  metadata: Record<string, unknown>;
  created_at: string;
  user_email?: string;
  user_name?: string;
}

interface InteractionsTableProps {
  data: Interaction[];
  isLoading: boolean;
}

const typeColors: Record<string, string> = {
  page_view: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  click: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  feature_usage: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  navigation: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
};

const PAGE_SIZE = 20;

export const InteractionsTable = ({ data, isLoading }: InteractionsTableProps) => {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [page, setPage] = useState(0);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  // Get all paths to resolve event titles
  const allPaths = data.map((item) => item.target);
  const { data: eventTitles = {} } = useEventTitles(allPaths);

  const filteredData = data.filter((item) => {
    const matchesSearch =
      search === "" ||
      item.target.toLowerCase().includes(search.toLowerCase()) ||
      item.user_email?.toLowerCase().includes(search.toLowerCase()) ||
      item.user_name?.toLowerCase().includes(search.toLowerCase());

    const matchesType = typeFilter === "all" || item.interaction_type === typeFilter;

    return matchesSearch && matchesType;
  });

  const paginatedData = filteredData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>All Interactions</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[400px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>All Interactions ({data.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by target, user email, or name..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              className="pl-9"
            />
          </div>
          <Select
            value={typeFilter}
            onValueChange={(value) => {
              setTypeFilter(value);
              setPage(0);
            }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="page_view">Page views</SelectItem>
              <SelectItem value="click">Clicks</SelectItem>
              <SelectItem value="feature_usage">Feature usage</SelectItem>
              <SelectItem value="navigation">Navigation</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    No interactions found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((interaction) => (
                  <TableRow key={interaction.id}>
                    <TableCell className="whitespace-nowrap">
                      {format(new Date(interaction.created_at), "MMM d, HH:mm:ss")}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={typeColors[interaction.interaction_type] || ""}
                      >
                        {interaction.interaction_type.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px]">
                      <ClickableEventLink
                        path={interaction.target}
                        eventTitles={eventTitles}
                        onEventClick={setSelectedEventId}
                        maxLength={25}
                      />
                    </TableCell>
                    <TableCell>
                      {interaction.user_name || interaction.user_email || (
                        <span className="text-muted-foreground">Anonymous</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[200px]">
                      {Object.keys(interaction.metadata).length > 0 ? (
                        <span className="text-xs text-muted-foreground">
                          {JSON.stringify(interaction.metadata).slice(0, 50)}
                          {JSON.stringify(interaction.metadata).length > 50 && "..."}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, filteredData.length)} of{" "}
              {filteredData.length}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      <EventPreviewDialog
        eventId={selectedEventId}
        open={!!selectedEventId}
        onOpenChange={(open) => !open && setSelectedEventId(null)}
      />
    </Card>
  );
};
